import React, { useMemo, useState } from "react";
import { CloseOutlined, MenuOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useLandingLang } from "../../i18n/LandingLangContext";
import { LanguageSwitcher } from "../LanguageSwitcher/LanguageSwitcher";

// Icono Stethoscope (SVG inline para evitar dependencias externas)
function StethoscopeIcon({ size = 16, className = "" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      aria-hidden="true"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 3v5a4 4 0 0 0 8 0V3" />
      <path d="M14 7h2a3 3 0 0 1 3 3v5a5 5 0 0 1-10 0v-2" />
      <circle cx="19" cy="5" r="2" />
    </svg>
  );
}

export function Navbar({
  logoSrc,
  brandAlt = "REVI",
  sections = [],
  scrolled = false,
  onScrollToId,
  onOpenDemo,
}) {
  const navigate = useNavigate();
  const { t, lang } = useLandingLang();
  const [menuOpen, setMenuOpen] = useState(false);

  // El navbar en ES conserva EXACTAMENTE el layout original maquetado. EN/FR tienen
  // textos mas largos: usan una version compacta (menos gap/padding) para que todo
  // quepa en una sola linea y el logo nunca se pegue/solape con el menu.
  const compact = lang !== "es";
  const linkGap = compact ? "gap-5" : "gap-8";
  const blockGap = compact ? "gap-5" : "gap-8";
  const btnsMl = compact ? "ml-3" : "ml-6";
  const secBtnPad = compact ? "px-4" : "px-5";
  const priBtnPad = compact ? "px-4" : "px-6";

  const navItems = useMemo(() => sections || [], [sections]);

  const handleNav = (id) => {
    if (typeof onScrollToId === "function") onScrollToId(id);
    setMenuOpen(false);
  };

  const goAuth = () => {
    setMenuOpen(false);
    navigate("/auth");
  };

  const openDemo = () => {
    setMenuOpen(false);
    onOpenDemo?.();
  };

  return (
    <>
      {/* NAVBAR PRINCIPAL 
        Clases base: fixed w-full z-50 transition-all duration-500
        Scrolled: bg-white/95 backdrop-blur-xl shadow-lg py-3
        Top: bg-transparent py-6
      */}
      <nav
        className={`fixed w-full z-50 transition-all duration-500 ${
          scrolled
            ? "bg-white/95 backdrop-blur-xl shadow-lg py-3"
            : "bg-transparent py-6"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between gap-6">
          {/* LOGO */}
          <button
            type="button"
            className="flex items-center gap-2 group cursor-pointer border-0 bg-transparent p-0 shrink-0"
            onClick={() => handleNav("top")}
            aria-label="Ir al inicio"
          >
            {logoSrc ? (
              <img src={logoSrc} alt={brandAlt} className="h-10 w-auto" />
            ) : (
              // Plantilla: text-slate-700 font-black text-2xl uppercase
              <div className="flex items-center gap-2 text-slate-700 font-black text-2xl uppercase">
                REVI
              </div>
            )}
          </button>

          {/* DESKTOP MENU */}
          {/* Contenedor: hidden lg:flex items-center gap-8 text-[13px] font-bold uppercase tracking-wider */}
          <div
            className={`hidden lg:flex items-center ${blockGap} text-[13px] font-bold uppercase tracking-wider`}
          >
            {/* LINKS */}
            <div
              className={`flex items-center ${linkGap}`}
              aria-label="Navegación principal"
            >
              {navItems.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  // Plantilla Link: hover:text-sky-500 transition-colors font-black text-slate-700
                  className="border-0 bg-transparent cursor-pointer hover:text-sky-500 transition-colors font-black text-slate-700 uppercase tracking-wider text-[13px] whitespace-nowrap"
                  onClick={() => handleNav(item.id)}
                >
                  {item.tKey ? t(item.tKey) : item.label}
                </button>
              ))}
            </div>

            {/* BOTONES DESKTOP */}
            <div className={`flex items-center gap-3 ${btnsMl}`}>
              {/* Botón Secundario (Acceso): 
                  text-slate-500 px-5 py-2.5 rounded-xl border border-slate-200 hover:bg-white 
                  transition-all flex items-center gap-2 font-bold uppercase tracking-wider text-[11px] 
              */}
              <button
                type="button"
                className={`text-slate-500 ${secBtnPad} py-2.5 rounded-xl border border-slate-200 hover:bg-white transition-all flex items-center gap-2 font-bold uppercase tracking-wider text-[11px] cursor-pointer whitespace-nowrap`}
                onClick={goAuth}
              >
                <StethoscopeIcon size={14} className="text-sky-500" />
                {t("proAccess")}
              </button>

              {/* Botón Primario (Solicitar):
                  bg-gradient-to-r from-sky-400 to-blue-500 text-white px-6 py-2.5 rounded-xl 
                  hover:shadow-lg hover:shadow-sky-100 transition-all font-bold uppercase tracking-wider text-[11px]
              */}
              <button
                type="button"
                className={`bg-gradient-to-r from-sky-400 to-blue-500 text-white ${priBtnPad} py-2.5 rounded-xl hover:shadow-lg hover:shadow-sky-100 transition-all font-bold uppercase tracking-wider text-[11px] border-0 cursor-pointer whitespace-nowrap`}
                onClick={openDemo}
              >
                {t("requestInfo")}
              </button>

              {/* Selector de idioma (ES/EN/FR) */}
              <div className="flex items-center pl-3 ml-1 border-l border-slate-200">
                <LanguageSwitcher size={26} />
              </div>
            </div>
          </div>

          {/* MOBILE TOGGLE (Hamburguesa) */}
          <button
            type="button"
            className="lg:hidden text-slate-500 border-0 bg-transparent cursor-pointer flex items-center justify-center"
            aria-label={menuOpen ? "Cerrar menú" : "Abrir menú"}
            onClick={() => setMenuOpen((v) => !v)}
          >
            {menuOpen ? (
              // Ajustamos tamaño si es necesario, la plantilla usa size={28}
              <CloseOutlined style={{ fontSize: "28px" }} />
            ) : (
              <MenuOutlined style={{ fontSize: "28px" }} />
            )}
          </button>
        </div>
      </nav>

      {/* MOBILE MENU OVERLAY */}
      {/* fixed inset-0 z-40 bg-white pt-24 px-8 lg:hidden animate-in fade-in slide-in-from-top-5 duration-300 text-center */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-40 bg-white pt-24 px-8 lg:hidden animate-in fade-in slide-in-from-top-5 duration-300 text-center"
          role="dialog"
          aria-label="Menú móvil"
        >
          {/* Botón cerrar flotante (opcional, la plantilla lo tiene integrado en el toggle del nav, 
              pero si el nav está encima (z-50) no hace falta repetirlo aquí dentro, 
              aunque por accesibilidad a veces se pone) */}

          <div className="flex flex-col gap-6 text-lg font-bold">
            {navItems.map((item) => (
              <button
                key={item.id}
                type="button"
                className="border-0 bg-transparent cursor-pointer text-slate-700 hover:text-sky-500 transition-colors font-black uppercase text-lg"
                onClick={() => handleNav(item.id)}
              >
                {item.tKey ? t(item.tKey) : item.label}
              </button>
            ))}

            <div className="flex flex-col gap-3 mt-4 w-full">
              {/* Botón Secundario Móvil:
                  w-full py-4 rounded-2xl border border-slate-200 flex items-center justify-center gap-2 
                  font-bold uppercase tracking-widest text-xs
              */}
              <button
                type="button"
                className="w-full py-4 rounded-2xl border border-slate-200 flex items-center justify-center gap-2 font-bold uppercase tracking-widest text-xs text-slate-700 bg-transparent cursor-pointer"
                onClick={goAuth}
              >
                <StethoscopeIcon size={18} className="text-sky-500" />
                {t("proAccess")}
              </button>

              {/* Botón Primario Móvil:
                  w-full py-4 rounded-2xl bg-gradient-to-r from-sky-400 to-blue-500 text-white shadow-lg 
                  font-bold uppercase tracking-widest text-xs
              */}
              <button
                type="button"
                className="w-full py-4 rounded-2xl bg-gradient-to-r from-sky-400 to-blue-500 text-white shadow-lg font-bold uppercase tracking-widest text-xs border-0 cursor-pointer"
                onClick={openDemo}
              >
                {t("requestInfo")}
              </button>

              {/* Selector de idioma (movil) */}
              <div className="flex items-center justify-center gap-3 mt-4">
                <LanguageSwitcher
                  size={40}
                  onSelect={() => setMenuOpen(false)}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
